import {
  TextInput, NumberInput, SelectInput, AutocompleteArrayInput, required, regex, useGetList, FormDataConsumer,
} from 'react-admin';
import { Box, Alert } from '@mui/material';
import { layout, WEBSITE_FAMILY_LABELS, WEBSITE_AVAILABILITY_LABELS } from '../styles/ds';
import { hasScript, crmSizeKey } from './websiteApi';
import { useCrmAttributeOptions, choicesOf } from './formHelpers';

const slugRule = regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'אותיות אנגליות קטנות, ספרות ומקפים בלבד (לדוגמה: simple-tefillin-ari)');

/**
 * Listing fields shared by Create and the edit page's details card.
 * `family` is chosen once on create and is immutable afterwards (backend rule).
 */
const WebsiteListingFields = ({ isCreate = false, family: fixedFamily }) => {
  const { data: categories = [] } = useGetList('website_categories', {
    pagination: { page: 1, perPage: 1000 }, sort: { field: 'sort_position', order: 'ASC' },
  });
  // Script and size are picked from the CRM's own attribute lists, never typed.
  const crmScripts = useCrmAttributeOptions('script');
  const crmSizes = useCrmAttributeOptions('size');
  const crmHeights = useCrmAttributeOptions(crmSizeKey('megillah'));
  return (
    <>
      <Box sx={layout.formGrid}>
        {isCreate && (
          <SelectInput source="family" label="משפחת מוצר" fullWidth validate={required('שדה חובה')}
            choices={Object.entries(WEBSITE_FAMILY_LABELS).map(([id, name]) => ({ id, name }))}
            helperText="לא ניתן לשנות אחרי היצירה" />
        )}
        <SelectInput source="category_id" label="קטגוריית אתר ראשית" fullWidth validate={required('שדה חובה')}
          choices={categories.map(c => ({ id: c.id, name: c.name_en }))}
          helperText="קובעת את כתובת הקטגוריה של המוצר; המוצר מוצג רק כשהיא מפורסמת" />
        <FormDataConsumer>
          {({ formData }) => (
            <AutocompleteArrayInput source="extra_category_ids" label="קטגוריות נוספות" fullWidth
              choices={categories.filter(c => c.id !== formData.category_id).map(c => ({ id: c.id, name: c.name_en }))}
              helperText="אותו מוצר יוצג גם בקטגוריות אלה, בלי לשכפל אותו (WD-028)" />
          )}
        </FormDataConsumer>
        <TextInput source="name_en" label="שם באתר (אנגלית)" fullWidth validate={required('שדה חובה')} />
        <TextInput source="slug" label="כתובת (slug)" fullWidth validate={isCreate ? [slugRule] : [required('שדה חובה'), slugRule]}
          helperText={isCreate ? 'אפשר להשאיר ריק — תיווצר אוטומטית מהשם באנגלית' : 'חלק מכתובת הדף באתר — שינוי שובר קישורים קיימים'} />
        <FormDataConsumer>
          {({ formData }) => {
            const family = fixedFamily ?? formData.family;
            if (!hasScript(family)) return null;
            return (
              <SelectInput source="script_code" label="כתב" fullWidth validate={required('שדה חובה')}
                choices={choicesOf(crmScripts, formData.script_code)}
                helperText={family === 'mezuzah' || family === 'megillah'
                  ? `מתוך רשימת הכתבים ב-CRM. לכל כתב ומידה — ${family === 'megillah' ? 'מגילה' : 'מזוזה'} אחת בלבד`
                  : 'מתוך רשימת הכתבים ב-CRM. לכל כתב — מוצר פשוטות אחד בלבד (WD-035)'} />
            );
          }}
        </FormDataConsumer>
        <FormDataConsumer>
          {({ formData }) => ((fixedFamily ?? formData.family) === 'mezuzah' ? (
            <SelectInput source="fixed_selections.size" label="מידה" fullWidth
              validate={required('למזוזה נדרשת מידה אחת')} choices={choicesOf(crmSizes, formData.fixed_selections?.size)}
              helperText="מתוך רשימת המידות ב-CRM. כל מזוזה באתר היא כתב אחד ומידה אחת; הלקוח בוחר רק רמת הידור. כדאי לציין את המידה בשם המוצר (למשל Mezuzah Ari 12 cm)" />
          ) : (fixedFamily ?? formData.family) === 'megillah' ? (
            <SelectInput source="fixed_selections.size" label="גובה קלף (ס&quot;מ)" fullWidth
              validate={required('למגילה נדרש גובה אחד')} choices={choicesOf(crmHeights, formData.fixed_selections?.size)}
              helperText="מתוך רשימת הגבהים ב-CRM (תכונת CM). כל מגילה באתר היא כתב אחד וגובה אחד; הלקוח בוחר רק רמת הידור. כדאי לציין את הגובה בשם המוצר (למשל Megillat Esther Ari 48 cm)" />
          ) : (fixedFamily ?? formData.family) === 'simple_tefillin' ? (
            <SelectInput source="fixed_selections.size" label="מידה קבועה" fullWidth
              validate={required('לתפילין פשוטות נדרשת מידה קבועה אחת')} choices={choicesOf(crmSizes, formData.fixed_selections?.size)}
              helperText="מתוך רשימת המידות ב-CRM. לשימוש פנימי בלבד: המידה אינה מוצגת ללקוח באתר בשום מקום — אין בחירת מידה ואין טקסט מידה (WD-036), ולכן אין לציין אותה גם בתיאור. כל הווריאנטים הממופים חייבים להיות במידה זו" />
          ) : null)}
        </FormDataConsumer>
        <SelectInput source="availability" label="זמינות באתר" fullWidth defaultValue="available"
          choices={Object.entries(WEBSITE_AVAILABILITY_LABELS).map(([id, name]) => ({ id, name }))} />
        <NumberInput source="sort_position" label="סדר תצוגה" fullWidth defaultValue={0} />
      </Box>
      <TextInput source="description" label="תיאור (אנגלית)" multiline minRows={3} fullWidth />
      <TextInput source="additional_information" label="מידע נוסף (אנגלית)" multiline minRows={2} fullWidth />
      <FormDataConsumer>
        {({ formData }) => {
          const family = fixedFamily ?? formData.family;
          if (family === 'price_on_request') return (
            <Alert severity="info" sx={{ width: '100%' }}>
              מוצר בלי מחיר ובלי עגלה: באתר מוצגים השם, התיאור וכפתור וואטסאפ לבירור מחיר. אין צורך בשורות או במחירים — אפשר לפרסם מיד.
            </Alert>
          );
          if (family === 'other') return (
            <Alert severity="warning" sx={{ width: '100%' }}>
              למוצרים מסוג "אחר" אין כלל תמחור מאושר — ניתן לשמור כטיוטה בלבד, ללא מחירים ופרסום.
            </Alert>
          );
          return null;
        }}
      </FormDataConsumer>
    </>
  );
};

export default WebsiteListingFields;
